/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

window.addEventListener("DOMContentLoaded", () => {
  function removeFirstPageParamAndOrigin(el) {
    if(el && el.href) {
      let href = el.href;

      if(href.startsWith(window.location.origin)) {
        href = href.replace(window.location.origin, '');
      }

      if(href.endsWith('?page=1')) {
        href = href.replace('?page=1', '');
      }

      el.setAttribute('href', href);
    }
  }

  // Initialize
  Array.prototype.forEach.call(document.querySelectorAll('[rel="prev"]'), removeFirstPageParamAndOrigin);

  // Mutation observer: Change any rel="prev" links that are added to the DOM
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      Array.prototype.forEach.call(mutation.addedNodes, function(addedNode) {
        if (addedNode.nodeType === Node.ELEMENT_NODE) {
          Array.prototype.forEach.call(addedNode.querySelectorAll('[rel="prev"]'), removeFirstPageParamAndOrigin);
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
});

/* Track form submissions */
const addFormSubmitListener = (formEmbed) => {
  const form = formEmbed.shadowRoot.querySelector('form');
  if (form) {
    form.addEventListener('submit', (event) => {
      const data = {
        form_id: form.id,
        form_name: form.name,
        form_action: form.action,
        form_method: form.method,
        form_data: Array.from(form.elements).reduce((acc, el) => {
          if (el.name || el.id) {
            acc[el.name || el.id] = el.value;
          }
          return acc;
        }, {}),
      }
      Shopify.analytics.publish('form_submitted_custom', data);
    }, { capture: true });
  }
};

// Handle any form-embeds already on the page
const existingFormEmbeds = Array.from(document.querySelectorAll('form-embed'));
existingFormEmbeds.forEach(addFormSubmitListener);

// Watch for new form-embeds being added
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeName === 'FORM-EMBED') {
        addFormSubmitListener(node);
      }
    });
  });
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});

if (!window.customElements.get('product-variants-scroll')) {
  class ProductVariantsScroll extends HTMLElement {
    connectedCallback() {
      this.container = this.querySelector('.product-variants-scroll-container');
      this.prevButton = this.querySelector('.product-variants-scroll-arrow--prev');
      this.nextButton = this.querySelector('.product-variants-scroll-arrow--next');
      this.track = this.querySelector('.product-variants-scroll-track');
      this.thumb = this.querySelector('.product-variants-scroll-thumb');

      if (!this.container || !this.prevButton || !this.nextButton) {
        return;
      }

      this.prevButton.addEventListener('click', () => this.scrollByDirection(-1));
      this.nextButton.addEventListener('click', () => this.scrollByDirection(1));
      this.container.addEventListener('scroll', () => this.updateButtons(), { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.updateButtons());
      this.resizeObserver.observe(this.container);

      // Since the native scrollbar is hidden in favor of our own track/thumb,
      // we need to re-implement click-and-drag on the thumb and click-to-jump
      // on the track ourselves.
      if (this.thumb) {
        this.thumb.addEventListener('pointerdown', this._onThumbPointerDown.bind(this));
      }
      if (this.track) {
        this.track.addEventListener('pointerdown', this._onTrackPointerDown.bind(this));
      }

      this.updateButtons();
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    _onThumbPointerDown(event) {
      event.preventDefault();
      event.stopPropagation();

      const rtl = getComputedStyle(this.container).direction === 'rtl' ? -1 : 1;
      const maxScroll = this.container.scrollWidth - this.container.clientWidth;
      const trackWidth = this.track.clientWidth;
      const thumbWidth = this.thumb.getBoundingClientRect().width;
      const availableTrackWidth = trackWidth - thumbWidth;
      const startX = event.clientX;
      const startScrollLeft = this.container.scrollLeft;

      this.thumb.classList.add('is-dragging');
      try {
        this.thumb.setPointerCapture(event.pointerId);
      } catch (error) {
        // Some browsers/pointer types may not support capture; dragging still
        // works via the pointermove/pointerup listeners below.
      }

      const onPointerMove = (moveEvent) => {
        if (availableTrackWidth <= 0) {
          return;
        }
        const deltaX = moveEvent.clientX - startX;
        const scrollDelta = rtl * (deltaX / availableTrackWidth) * maxScroll;
        this.container.scrollLeft = Math.max(0, Math.min(maxScroll, startScrollLeft + scrollDelta));
      };

      const onPointerUp = () => {
        this.thumb.classList.remove('is-dragging');
        this.thumb.removeEventListener('pointermove', onPointerMove);
        this.thumb.removeEventListener('pointerup', onPointerUp);
        this.thumb.removeEventListener('pointercancel', onPointerUp);
      };

      this.thumb.addEventListener('pointermove', onPointerMove);
      this.thumb.addEventListener('pointerup', onPointerUp);
      this.thumb.addEventListener('pointercancel', onPointerUp);
    }

    _onTrackPointerDown(event) {
      if (event.target === this.thumb) {
        return;
      }

      const rtl = getComputedStyle(this.container).direction === 'rtl' ? -1 : 1;
      const maxScroll = this.container.scrollWidth - this.container.clientWidth;
      const trackRect = this.track.getBoundingClientRect();
      const thumbWidth = this.thumb.getBoundingClientRect().width;
      const availableTrackWidth = trackRect.width - thumbWidth;

      if (availableTrackWidth <= 0) {
        return;
      }

      const clickX = event.clientX - trackRect.left - thumbWidth / 2;
      const ratio = Math.max(0, Math.min(1, clickX / availableTrackWidth));

      this.container.scrollTo({
        left: rtl * ratio * maxScroll,
        behavior: 'smooth'
      });
    }

    scrollByDirection(direction) {
      const item = this.container.querySelector('.product-variant-box');
      const amount = item ? item.getBoundingClientRect().width + 10 : 80;
      const rtl = getComputedStyle(this.container).direction === 'rtl' ? -1 : 1;

      this.container.scrollBy({
        left: rtl * direction * amount,
        behavior: 'smooth'
      });
    }

    updateButtons() {
      const variantCount = this.container.querySelectorAll('.product-variant-box').length;
      const maxScroll = this.container.scrollWidth - this.container.clientWidth;
      const canScroll = maxScroll > 1;
      const showArrows = variantCount > 3 && canScroll;

      this.classList.toggle('is-scrollable', showArrows);

      if (!showArrows) {
        this.prevButton.disabled = true;
        this.nextButton.disabled = true;
        this.updateThumb(0, 1);
        return;
      }

      const scrollLeft = Math.abs(this.container.scrollLeft);
      this.prevButton.disabled = scrollLeft <= 1;
      this.nextButton.disabled = scrollLeft >= maxScroll - 1;
      this.updateThumb(scrollLeft, maxScroll);
    }

    updateThumb(scrollLeft, maxScroll) {
      if (!this.thumb) {
        return;
      }

      const widthPercent = Math.min(100, (this.container.clientWidth / this.container.scrollWidth) * 100);
      const scrollableWidthPercent = 100 - widthPercent;
      const scrollRatio = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      const leftPercent = scrollableWidthPercent * scrollRatio;

      this.thumb.style.width = `${widthPercent}%`;
      this.thumb.style.left = `${leftPercent}%`;
    }
  }

  window.customElements.define('product-variants-scroll', ProductVariantsScroll);
}